import {useMemo} from 'react'
import type {DashboardSectionConfig} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'

import {getAvailableInboxSections} from '../constants/inbox-sections'
import {
  arraysEqual,
  useOptionalPullsDashboardPayload,
  useOptionalPullsDashboardRawPayload,
} from '../contexts/PullsDashboardSurfaceStateContext'
import {normalizeInboxSectionPreferences} from '../helpers/inbox-section-preferences'
import {getAvailableInboxSectionIds, isInboxSectionId} from '../routes/pulls-dashboard-inbox-route'

export interface VisibleInboxSections {
  visibleSections: DashboardSectionConfig[]
  firstVisibleSectionId: string
}

/**
 * Computes the inbox sections that should currently be rendered, applying the
 * user's hidden-section and ordering preferences (with optimistic-preference
 * handling) on top of the feature-gated set of available sections.
 *
 * Shared by the live inbox (`PullsDashboardInboxComponent`) and its loading
 * skeleton so both render the same sections in the same order.
 */
export function useVisibleInboxSections(): VisibleInboxSections {
  const payload = useOptionalPullsDashboardPayload()
  const rawPayload = useOptionalPullsDashboardRawPayload()
  const rawSectionOrder = payload?.sectionOrder
  const rawHiddenSections = payload?.hiddenSections
  const inboxSectionQueriesFromPayload = payload?.inboxSectionQueries

  // Fall back to the optimistic payload when no separate rawPayload is
  // provided.
  const hasOptimisticSectionPreferences =
    rawPayload &&
    payload &&
    (!arraysEqual(payload.sectionOrder ?? [], rawPayload.sectionOrder ?? []) ||
      !arraysEqual(payload.hiddenSections ?? [], rawPayload.hiddenSections ?? []))
  const firstVisiblePayload = hasOptimisticSectionPreferences ? payload : (rawPayload ?? payload)

  const firstVisibleSectionOrder = firstVisiblePayload?.sectionOrder
  const firstVisibleHiddenSections = firstVisiblePayload?.hiddenSections
  const sectionOrder = useMemo(() => rawSectionOrder ?? [], [rawSectionOrder])
  const hiddenSections = useMemo(() => rawHiddenSections ?? [], [rawHiddenSections])
  const firstVisibleOrder = useMemo(() => firstVisibleSectionOrder ?? [], [firstVisibleSectionOrder])
  const firstVisibleHidden = useMemo(() => firstVisibleHiddenSections ?? [], [firstVisibleHiddenSections])
  const availableSectionIds = useMemo(() => getAvailableInboxSectionIds(), [])

  // `inboxSectionQueries` is the source of truth for sections that can render
  // right now. The backend removes hidden sections from this map.
  const renderableSectionIds = useMemo(() => {
    if (inboxSectionQueriesFromPayload === undefined) return availableSectionIds
    return Object.keys(inboxSectionQueriesFromPayload)
  }, [availableSectionIds, inboxSectionQueriesFromPayload])

  const availableSections = useMemo(() => getAvailableInboxSections(renderableSectionIds), [renderableSectionIds])
  const renderableAvailableSectionIds = useMemo(() => availableSections.map(section => section.id), [availableSections])
  const normalizedVisiblePreferences = useMemo(
    () =>
      normalizeInboxSectionPreferences({
        order: sectionOrder,
        hidden: hiddenSections,
        availableIds: renderableAvailableSectionIds,
      }),
    [hiddenSections, renderableAvailableSectionIds, sectionOrder],
  )

  const visibleSections = useMemo(() => {
    const hidden = new Set(normalizedVisiblePreferences.hiddenSections)
    // Filter out any ids unknown to the query registry as a runtime guard
    // against drift between `inboxSections` and `INBOX_SECTION_DEFINITIONS`.
    const userVisible = availableSections.filter(section => !hidden.has(section.id) && isInboxSectionId(section.id))

    // Sort by sectionOrder index. Sections missing from sectionOrder (e.g. new
    // sections launched after a user customized) keep their relative order at the
    // end via a stable sort with Infinity-fallback indexes.
    const orderIndex = new Map(normalizedVisiblePreferences.sectionOrder.map((id, idx) => [id, idx]))
    return [...userVisible].sort((a, b) => {
      const ai = orderIndex.get(a.id) ?? Number.POSITIVE_INFINITY
      const bi = orderIndex.get(b.id) ?? Number.POSITIVE_INFINITY
      return ai - bi
    })
  }, [availableSections, normalizedVisiblePreferences])

  // Derive the first-visible section id from the server payload, except while
  // optimistic section preferences are active. During that window, the rendered
  // section list and query-name selection must agree on order and hidden state.
  // Pass the available section ids so a saved order
  // beginning with a currently disabled section doesn't cause the actual top
  // section to be deferred. This intentionally does not use
  // `inboxSectionQueries`, because hidden sections are removed from that map.
  const normalizedFirstVisiblePreferences = useMemo(
    () =>
      normalizeInboxSectionPreferences({
        order: firstVisibleOrder,
        hidden: firstVisibleHidden,
        availableIds: availableSectionIds,
      }),
    [availableSectionIds, firstVisibleHidden, firstVisibleOrder],
  )

  return {visibleSections, firstVisibleSectionId: normalizedFirstVisiblePreferences.firstVisibleSectionId}
}
